<template>
  <div class="elevator-3d">
    <!-- 使用SVG电梯模型 -->
    <div class="elevator-svg-container">
      <!-- 腾讯地图显示 -->
      <div id="tencent-map-container" class="map-container"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ElevatorModel3D",
  props: {
    inspectionPoints: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      map: null,
      mapKey: "FR2BZ-JQ4NQ-GWI5J-2X6PE-K4MV7-4LFCV",
      markers: [], // 存储MultiMarker实例，初始化为空数组
      markerCluster: null, // 存储点聚合实例
      infoWindow: null,
      currentMarkerInfo: null, // 保存当前点击的marker信息
      // 地图状态保存
      mapCenter: null,
      mapZoom: null,
      savedInspectionPoints: [], // 保存原始点位数据
    };
  },
  watch: {
    inspectionPoints: {
      immediate: true,
      deep: true,
      handler(newVal) {
        // 保存原始点位数据
        if (newVal && newVal.length > 0) {
          this.savedInspectionPoints = newVal;

          // 如果地图已初始化，直接创建标记点
          if (this.map) {
            this.createOptimizedMarkers(newVal);
          }
          // 如果地图未初始化，会在mounted或activated中自动创建
        }
      },
    },
  },
  mounted() {
    // 只有在没有地图实例时才初始化，避免重复创建
    if (!this.map) {
      this.loadTencentMap();
    }
  },
  activated() {
    // Vue keep-alive 激活时调用，处理从HomeViewTwo返回的情况
    if (this.map) {
      // 确保地图控件存在
      if (TMap.controls) {
        const existingControls = this.map.getControls();
        const hasZoom = existingControls.some(control => control instanceof TMap.controls.Zoom);
        const hasScale = existingControls.some(control => control instanceof TMap.controls.Scale);

        if (!hasZoom) {
          this.map.addControl(new TMap.controls.Zoom({}));
        }
        if (!hasScale) {
          this.map.addControl(new TMap.controls.Scale({}));
        }
      }

      // 强制重新创建标记点，确保数据最新
      // 先清理现有标记点
      if (Array.isArray(this.markers) && this.markers.length > 0) {
        this.markers.forEach((marker) => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
        this.markers = [];
      }

      // 重新创建标记点
      if (this.savedInspectionPoints && this.savedInspectionPoints.length > 0) {
        this.createOptimizedMarkers(this.savedInspectionPoints);
      } else if (this.inspectionPoints && this.inspectionPoints.length > 0) {
        this.createOptimizedMarkers(this.inspectionPoints);
      }
    } else if (this.savedInspectionPoints && this.savedInspectionPoints.length > 0) {
      // 如果地图实例丢失但有点位数据，重新初始化地图
      this.loadTencentMap();
    } else if (this.inspectionPoints && this.inspectionPoints.length > 0) {
      // 如果没有保存的点位数据但有当前props数据，也重新初始化
      this.savedInspectionPoints = this.inspectionPoints;
      this.loadTencentMap();
    }
  },
  beforeDestroy() {
    // 清理地图实例
    if (this.map) {
      // 解绑地图事件 - 使用保存的处理函数引用
      if (this.map.off) {
        if (this.centerChangedHandler) {
          this.map.off("center_changed", this.centerChangedHandler);
          this.centerChangedHandler = null;
        }
        if (this.zoomChangedHandler) {
          this.map.off("zoom_changed", this.zoomChangedHandler);
          this.zoomChangedHandler = null;
        }
      }

      // 清理所有标记点
      if (Array.isArray(this.markers) && this.markers.length > 0) {
        this.markers.forEach((marker) => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
        this.markers = [];
      }
      this.map = null;
    }
  },
  methods: {
    loadTencentMap() {
      // 动态加载腾讯地图API
      if (window.TMap) {
        this.initMap();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${this.mapKey}`;
      script.onload = () => {
        this.initMap();
      };
      script.onerror = () => {
        // 腾讯地图API加载失败
      };
      document.head.appendChild(script);
    },
    initMap() {
      // 初始化地图 - 尝试恢复之前的状态，否则使用默认设置
      // 广州市坐标：纬度23.1291，经度113.2644
      let centerLat = 23.1291;
      let centerLng = 113.2644;
      let zoomLevel = 10;

      // 保存原始点位数据
      this.savedInspectionPoints = this.inspectionPoints || [];

      // 优先使用props传入的inspectionPoints数据
      const pointsToUse = this.savedInspectionPoints;

      // 如果有保存的地图状态，优先使用保存的状态
      if (this.mapCenter && this.mapZoom) {
        centerLat = this.mapCenter.lat;
        centerLng = this.mapCenter.lng;
        zoomLevel = this.mapZoom;
      } else if (pointsToUse.length > 0) {
        // 如果没有保存的状态，使用第一个点位作为中心
        const firstPoint = pointsToUse[0].lnglat.split(",");
        centerLng = parseFloat(firstPoint[0]);
        centerLat = parseFloat(firstPoint[1]);
        zoomLevel = 12;
      } else {
        // 如果没有点位数据，使用广州市中心
        centerLat = 23.1291;
        centerLng = 113.2644;
        zoomLevel = 10;
      }

      const center = new TMap.LatLng(centerLat, centerLng);
      this.map = new TMap.Map(document.getElementById("tencent-map-container"), {
        center: center,
        zoom: zoomLevel,
        viewMode: "2D",
      });

      // 绑定地图移动和缩放事件，保存状态
      this.bindMapEvents();

      // 添加地图控件 - 使用正确的控件名称
      if (TMap.controls) {
        this.map.addControl(new TMap.controls.Zoom({}));
        this.map.addControl(new TMap.controls.Scale({}));
      }

      // 智能创建标记点：根据数量选择最佳渲染方式
      this.createOptimizedMarkers(pointsToUse);
    },
    bindMapEvents() {
      // 绑定地图移动和缩放事件，保存状态
      if (this.map) {
        // 保存事件监听器引用，以便后续移除
        this.centerChangedHandler = () => {
          if (this.map) {
            const center = this.map.getCenter();
            this.mapCenter = { lat: center.getLat(), lng: center.getLng() };
          }
        };

        this.zoomChangedHandler = () => {
          if (this.map) {
            this.mapZoom = this.map.getZoom();
          }
        };

        this.map.on("center_changed", this.centerChangedHandler);
        this.map.on("zoom_changed", this.zoomChangedHandler);
      }
    },

    // 智能创建标记点：根据数量选择最佳渲染方式
    createOptimizedMarkers(points) {
      if (!points || points.length === 0) {
        return;
      }

      // 暂时注释点聚合功能，只保留marker点击事件
      // if (points.length > 50) {
      //   this.createMarkerCluster(points);
      // }
      // 由于点聚合被禁用，始终创建单个标记点
      this.createIndividualMarkers(points);
    },
    createIndividualMarkers(points) {
      // 如果没有点位数据，则不创建标记
      if (!points || points.length === 0) {
        console.log('[地图调试] 没有点位数据');
        return;
      }

      // 转换点位数据格式 - 适配TMap.MultiMarker的geometries格式
      const geometries = points.map((point, index) => {
        const [lng, lat] = point.lnglat.split(",").map(Number);
        // 强制所有marker使用蓝色样式进行排查
        const styleId = point.status == '1' ? 'blue' : "red";

        return {
          id: point.id ? point.id.toString() : Math.random().toString(),
          styleId: styleId,
          position: new TMap.LatLng(lat, lng),
          properties: {
            title: point.area_name || "",
            id: point.id || "",
            area_name: point.area_name || "",
            status: point.status || "",
          },
        };
      });

      // 清理之前的标记
      if (Array.isArray(this.markers) && this.markers.length > 0) {
        this.markers.forEach((marker) => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
        this.markers = [];
      }

      // 为每个geometry设置自定义图标 - 使用小尺寸图标
      geometries.forEach(geometry => {
        const isBlue = geometry.styleId == 'blue';
        // 设置样式ID，对应styles中的配置
        geometry.styleId = isBlue ? 'blue' : 'red';
        // 不需要设置geometry.icon，样式在styles中统一管理
      });

      this.markers = new TMap.MultiMarker({
        map: this.map,
        styles: {
          // 定义蓝色样式 - 小尺寸
          blue: new TMap.MarkerStyle({
            width: 20,
            height: 22,
            anchor: { x: 10, y: 10 }, // 锚点居中
            src: require('@/assets/images/blue-icon.png'),
          }),
          // 红色样式
          red: new TMap.MarkerStyle({
            width: 20,
            height: 22,
            anchor: { x: 10, y: 10 },
            src: require('@/assets/images/red-icon.png'),
          })
        },
        geometries: geometries,
        enableDefaultStyle: false
      });

      // 绑定标记点点击事件
      const handler = (evt) => {
        let geometry =
          evt.geometry ||
          (evt.detail && evt.detail.geometry) ||
          (evt.target && evt.target.geometry);
        if (geometry && geometry.properties) {
          this.handleMarkerClick({
            properties: geometry.properties,
            position: geometry.position,
          });
        }
      };

      // 绑定标记点点击事件（兼容不同版本API）
      if (typeof this.markers.on === "function") {
        this.markers.on("click", handler);
      } else if (typeof this.markers.addListener === "function") {
        this.markers.addListener("click", handler);
      }
    },
    handleMarkerClick(marker) {
      // 处理标记点点击事件
      // 点击marker后不需要显示area_name（地址），但显示其他信息
      this.showMarkerInfo(marker);
    },
    showMarkerInfo(marker) {
      // 显示标记点信息（不包含area_name地址）
      const { id, title } = marker.properties;
      const markerId = id || "";

      // 创建信息窗口内容（不包含area_name地址），添加跳转按钮
      // 直接在按钮上存储title信息，避免后续匹配问题
      const markerTitle = title || "未知区域";
      const infoContent = '<div style="padding: 10px;"><h4 style="margin: 0 0 8px 0; color: #333;">' + (title || "标记点") + '</h4><button id="marker-detail-btn" data-marker-id="' + markerId + '" data-marker-title="' + encodeURIComponent(markerTitle) + '" style="margin-top: 10px; padding: 6px 12px; background: #1890ff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;">查看详情</button></div>';

      // 移除之前的信息窗口
      if (this.infoWindow) {
        this.infoWindow.close();
      }

      // 创建新的信息窗口
      this.infoWindow = new TMap.InfoWindow({
        map: this.map,
        position: marker.position,
        content: infoContent,
        zIndex: 1000,
      });

      this.infoWindow.open();

      // 添加按钮点击事件（使用setTimeout确保DOM已渲染）
      setTimeout(() => {
        const btn = document.getElementById("marker-detail-btn");
        if (btn) {
          btn.addEventListener("click", (e) => {
            const clickedMarkerId = e.target.getAttribute("data-marker-id");
            const clickedMarkerTitle = decodeURIComponent(e.target.getAttribute("data-marker-title") || "");
            this.navigateToHomeViewTwo(clickedMarkerId, clickedMarkerTitle);
          });
        }
      }, 100);
    },
navigateToHomeViewTwo(markerId, markerTitle = "未知区域") {
  // 跳转到HomeViewTwo页面，需要根据marker的name匹配首页搜索数据的id
  // markerTitle参数直接从按钮点击时传入，确保准确性
  
  // 优先使用localStorage中的数据匹配，因为ElevatorModel3D和HomeView可能是同级组件
  let matchedId = markerId;
  
  try {
    const storedCommunityData = localStorage.getItem('communityData');
    
    if (storedCommunityData) {
      const communityData = JSON.parse(storedCommunityData);
      
      if (Array.isArray(communityData) && communityData.length > 0) {
        const matchedProject = communityData.find(
          (item) => item.name === markerTitle
        );
        
        if (matchedProject) {
          matchedId = matchedProject.id;
        }
        // 如果没有匹配到，仍然使用原始markerId，不要阻止跳转
      }
    }
    // 如果localStorage中没有数据，仍然使用原始markerId进行跳转
  } catch (error) {
    console.error('读取communityData失败:', error);
    // 出错时仍然使用原始markerId，不要阻止跳转
  }
  
  // 确保$router存在再进行跳转 - 这是最重要的，不能让错误阻止跳转
  if (this.$router) {
    try {
      this.$router.push({
        name: "homeTwo",
        params: { id: matchedId },
        query: { name: markerTitle },
      });
    } catch (error) {
      console.error('Router push error:', error);
      // 即使路由跳转失败，也不要抛出错误阻止程序运行
    }
  }
},

    // 点聚合功能已暂时移除
    // 如需启用，请参考腾讯地图官方文档：https://lbs.qq.com/webApi/javascriptGL/glDoc/glDocMarker

    // 清理所有标记点
    clearAllMarkers() {
      // 清理普通标记
      if (Array.isArray(this.markers) && this.markers.length > 0) {
        this.markers.forEach((marker) => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
        this.markers = [];
      }

      // 暂时注释点聚合清理
      // if (this.markerCluster) {
      //   this.markerCluster.setMap(null);
      //   this.markerCluster = null;
      // }
    },

    // 点聚合测试功能已暂时移除
    // 调试方法：手动触发标记点创建
    debugCreateMarkers() {
      if (this.inspectionPoints && this.inspectionPoints.length > 0) {
        this.createOptimizedMarkers(this.inspectionPoints);
      } else {
        // 暂时注释点聚合测试
        // this.testMarkerCluster();
      }
    },
  },
};
</script>

<style scoped>
/* 3D电梯模型样式 */
.elevator-3d {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  perspective: 1000px;
}

.elevator-svg-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(10, 25, 41, 0.3);
  position: relative;
  overflow: hidden;
  /* border-radius: 15px; */
  /* border: 1px solid rgba(0, 212, 255, 0.3); */
  /* box-shadow: */
  /*   0 0 30px rgba(0, 212, 255, 0.2), */
  /*   inset 0 0 30px rgba(0, 212, 255, 0.1); */
}

.elevator-svg {
  width: 100%;
  height: 100%;
  /* filter: drop-shadow(0 0 10px rgba(0, 212, 255, 0.5)); */
}

/* 腾讯地图样式 */
.map-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
  border-radius: 8px;
  overflow: hidden;
}

/* 暂时注释点聚合样式
:deep(.cluster-marker-1) {
  background-color: #1890ff;
  border: 2px solid #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

:deep(.cluster-marker-2) {
  background-color: #52c41a;
  border: 2px solid #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

:deep(.cluster-marker-3) {
  background-color: #fa8c16;
  border: 2px solid #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
} */

/* 覆盖腾讯地图默认弹窗样式 */
:deep(.tmap-infowindow-content) {
  background: rgba(10, 25, 41, 0.9);
  color: #fff;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 4px;
  padding: 8px 12px;
}
</style>
